// SEO utility functions for managing meta tags dynamically
// This helps with SPA SEO for better search engine visibility

export const updateMetaTags = (config = {}) => {
  const {
    title = "Srilatha Rakesh",
    description = "Craft Your Life with Srilatha Rakesh - Expert life coaching for personal development",
    image = "https://www.srilatharakesh.com/images/og-image.png",
    url = "https://www.srilatharakesh.com/",
    author = "Srilatha Rakesh",
    type = "website",
    structuredData,
  } = config;

  document.title = title;

  updateMetaTag("name", "description", description);
  updateMetaTag("name", "author", author);
  updateMetaTag("property", "og:title", title);
  updateMetaTag("property", "og:description", description);
  updateMetaTag("property", "og:image", image);
  updateMetaTag("property", "og:url", url);
  updateMetaTag("property", "og:type", type);
  updateMetaTag("name", "twitter:title", title);
  updateMetaTag("name", "twitter:description", description);
  updateMetaTag("name", "twitter:image", image);

  updateCanonical(url);

  if (structuredData) {
    injectStructuredData(structuredData);
  }
};

const updateMetaTag = (attribute, name, content) => {
  let element = document.querySelector(`meta[${attribute}="${name}"]`);

  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attribute, name);
    document.head.appendChild(element);
  }

  element.setAttribute("content", content);
};

const updateCanonical = (url) => {
  let canonical = document.querySelector('link[rel="canonical"]');

  if (!canonical) {
    canonical = document.createElement("link");
    canonical.rel = "canonical";
    document.head.appendChild(canonical);
  }

  canonical.href = url;
};

export const injectStructuredData = (data, id = "structured-data") => {
  if (typeof document === "undefined") return;

  let existing = document.querySelector(`script[data-schema-id="${id}"]`);

  if (existing) {
    existing.remove();
  }

  const script = document.createElement("script");
  script.type = "application/ld+json";
  script.setAttribute("data-schema-id", id);
  script.textContent = JSON.stringify(data);
  document.head.appendChild(script);
};

// SEO configuration for different pages
export const pageConfig = {
  home: {
    title: "Srilatha Rakesh | Life Coaching & Personal Development",
    description:
      "Craft Your Life with Srilatha Rakesh. Expert life coach offering 1-on-1 coaching, group coaching, workshops, and digital support for personal growth and transformation.",
    url: "https://www.srilatharakesh.com/",
    type: "website",
  },
  about: {
    title: "About Srilatha Rakesh | Life Coach & Personal Development Expert",
    description:
      "Learn about Srilatha Rakesh's coaching philosophy, experience, and approach to helping clients craft their best lives through personalized coaching.",
    url: "https://www.srilatharakesh.com/#/about",
    type: "article",
  },
  services: {
    title: "Coaching Services | 1-on-1, Group, Workshops & Digital Support",
    description:
      "Explore our comprehensive coaching services: 1-on-1 personalized coaching, group coaching programs, interactive workshops, and digital support for continuous growth.",
    url: "https://www.srilatharakesh.com/#/services",
    type: "service",
  },
  procuralWellbeing: {
    title: "Procural Well-Being Program | Corporate Wellness Coaching",
    description:
      "Discover our Procural Well-Being program designed to enhance employee wellness, productivity, and quality of life through professional coaching.",
    url: "https://www.srilatharakesh.com/#/procural-well-being",
    type: "service",
  },
  contact: {
    title: "Contact Srilatha Rakesh | Book Your Coaching Session",
    description:
      "Ready to craft your life? Contact Srilatha Rakesh today to schedule your personalized coaching session and start your transformation journey.",
    url: "https://www.srilatharakesh.com/#/contact",
    type: "contact",
  },
  employeeFeedback: {
    title: "Employee Feedback Form | Your Coaching Experience Matters",
    description:
      "Share your valuable feedback about your coaching experience with Srilatha Rakesh. Your insights help us improve our services.",
    url: "https://www.srilatharakesh.com/#/employee-feedbackform",
    type: "form",
  },
};

// Generate schema.org structured data
export const generateSchemaMarkup = (type, data) => {
  const schemas = {
    person: {
      "@context": "https://schema.org",
      "@type": "Person",
      name: "Srilatha Rakesh",
      description:
        "Certified Life Coach dedicated to helping individuals discover their true potential and create meaningful change in their lives",
      url: "https://www.srilatharakesh.com/",
      image: "https://www.srilatharakesh.com/images/srilatha-rakesh.jpg",
      jobTitle: "Certified Life Coach",
      email: "info.srilatharakesh@gmail.com",
      telephone: "+91 8977895528",
      gender: "Female",
      areaServed: ["IN", "Global"],
      knowsAbout: [
        "Life Coaching",
        "Personal Development",
        "Career Coaching",
        "Wellness",
        "Self-Improvement",
        "Transformation",
        "1-on-1 Coaching",
        "Group Coaching",
        "Corporate Training",
      ],
      hasCredential: {
        "@type": "EducationalOccupationalCredential",
        name: "Certified Life Coach",
        credentialCategory: "Professional Certification",
      },
      sameAs: [
        "https://www.instagram.com/SrilathaRakesh.lifecoach",
        "https://www.srilatharakesh.com/",
      ],
      workLocation: {
        "@type": "Place",
        address: {
          "@type": "PostalAddress",
          addressLocality: "Hyderabad",
          addressRegion: "Telangana",
          postalCode: "500001",
          addressCountry: "IN",
        },
      },
    },
    service: {
      "@context": "https://schema.org",
      "@type": "Service",
      name: data.name || "Coaching Service",
      description: data.description || "Professional coaching service",
      provider: {
        "@type": "Person",
        name: "Srilatha Rakesh",
      },
      areaServed: ["IN", "Global"],
      availableChannel: {
        "@type": "ServiceChannel",
        serviceUrl: "https://www.srilatharakesh.com/",
      },
    },
    question: {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: data.faqs.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: faq.answer,
        },
      })),
    },
  };

  return schemas[type] || null;
};

// Add breadcrumb schema markup
export const generateBreadcrumbs = (breadcrumbs) => {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: breadcrumbs.map((crumb, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: crumb.name,
      item: crumb.url,
    })),
  };
};

// Trigger Google analytics page view (if using Google Analytics)
export const trackPageView = (pagePath) => {
  if (typeof window !== "undefined" && window.gtag) {
    window.gtag("config", "GA_MEASUREMENT_ID", {
      page_path: pagePath,
      page_title: document.title,
    });
  }
};
